import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getReactionWithParticipants } from '@peruselab/api-client/reactions';
import {
  aromaticBondIds,
  bondStereoValues,
  drawnBondOrders
} from '@peruselab/structure-editor/domain/documentSelectors';
import { drawingDocumentToSvg } from '@peruselab/structure-editor/rendering/svgExport';
import { reactionPaper } from '@peruselab/structure-editor/chemistry/reactionPaper';
import { reactionPlusSigns } from '@peruselab/structure-editor/domain/reactionBoxes';
import { SvgMarkup } from '../SvgMarkup';

/** The ink thumbnails use (isomerSvg), on the white paper every structure sits on. */
const INK = '#1f2933';
/** Room around the drawing, in drawing units. */
const PADDING = 8;
/** The paper a reaction saved before #1140 is laid out on (it has none of its own). */
const LAYOUT_PAPER = { width: 1000, height: 700 };

export type ReactionPictureState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly svg: string; readonly width: number; readonly height: number }
  | { readonly status: 'error'; readonly message: string };

/**
 * #1141 (ADR 0025 D1/D2/D5) -- a saved reaction as its paper draws it: the paper rebuilt as Open
 * rebuilds it (paperFromReaction -- the author's own layout since #1140, the generated one for an
 * older reaction), drawn by the export renderer and cropped to the drawing, with the paper's "+"
 * signs. The same picture the paper's Export makes, wherever a reaction is shown.
 */
export function useReactionPicture(reactionId: string): ReactionPictureState {
  const { t } = useTranslation();
  // Keyed by the reaction it is for: another reaction's answer reads as loading (no reset needed).
  const [loaded, setLoaded] = useState<{ readonly reactionId: string; readonly state: ReactionPictureState } | null>(
    null
  );
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const reaction = await getReactionWithParticipants(reactionId);
      const { document } = await reactionPaper(reaction, LAYOUT_PAPER);
      const { width, height } = document.depiction.coordinateSet;
      const svg = drawingDocumentToSvg(
        document,
        {
          bondOrders: drawnBondOrders(document),
          aromaticBondIds: aromaticBondIds(document),
          stereoBonds: bondStereoValues(document),
          overriddenBondIds: new Set()
        },
        INK,
        { width, height },
        `rx-${reactionId}-`.replace(/[^A-Za-z0-9_-]/g, ''),
        {},
        [],
        [],
        { geometry: 'authored', paddingPx: PADDING, plusSigns: reactionPlusSigns(document) }
      );
      const box = /viewBox="[-\d.]+ [-\d.]+ ([\d.]+) ([\d.]+)"/.exec(svg);
      if (!cancelled) {
        setLoaded({
          reactionId,
          state: { status: 'ready', svg, width: Number(box?.[1] ?? width), height: Number(box?.[2] ?? height) }
        });
      }
    })().catch((error: unknown) => {
      if (!cancelled) {
        setLoaded({
          reactionId,
          state: {
            status: 'error',
            message: error instanceof Error ? error.message : t('reactionThumbnail.renderError')
          }
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reactionId, t]);
  return loaded?.reactionId === reactionId ? loaded.state : { status: 'loading' };
}

/** The picture, scaled to fit its box (proportions kept). */
export function ReactionPicture({ state }: { readonly state: ReactionPictureState }) {
  const { t } = useTranslation();
  if (state.status === 'loading') {
    return <span className="isomer-thumbnail-status">{t('common.loading')}</span>;
  }
  if (state.status === 'error') {
    return <span className="isomer-thumbnail-status isomer-thumbnail-error">{state.message}</span>;
  }
  return (
    <div
      className="reaction-picture"
      role="img"
      aria-label={t('reactionResults.schemeLabel')}
      data-testid="reaction-picture"
    >
      <SvgMarkup className="reaction-picture-svg" svg={state.svg} />
    </div>
  );
}
